import { appointmentsCalendarPath } from '../modules/crm-appointments/paths';
import { todosCalendarPath } from '../modules/crm-todos/paths';
import type { SearchResponse } from '../services/searchService';

/** Display order for grouped global search results. */
export const GLOBAL_SEARCH_MODULE_ORDER = [
  'emails',
  'templates',
  'clients',
  'contacts',
  'todos',
  'appointments',
] as const;

export type GlobalSearchModuleKey = (typeof GLOBAL_SEARCH_MODULE_ORDER)[number];

export const GLOBAL_SEARCH_MODULE_LABELS: Record<string, string> = {
  emails: 'Emails',
  templates: 'Templates',
  clients: 'Address Book',
  contacts: 'People',
  todos: 'Todos',
  appointments: 'Appointments',
};

export const GLOBAL_SEARCH_MODULE_ROUTES: Record<string, string> = {
  emails: '/emails',
  templates: '/library',
  clients: '/clients',
  contacts: '/clients',
  todos: todosCalendarPath(),
  appointments: appointmentsCalendarPath(),
};

export const GLOBAL_SEARCH_MODULE_FILTERS: { key: GlobalSearchModuleKey; label: string }[] = [
  { key: 'emails', label: 'Emails' },
  { key: 'templates', label: 'Templates' },
  { key: 'clients', label: 'Address Book' },
  { key: 'contacts', label: 'People' },
  { key: 'todos', label: 'Todos' },
  { key: 'appointments', label: 'Appointments' },
];

export interface FlatSearchRecord {
  moduleKey: string;
  path: string;
  label: string;
  subtitle?: string;
  typeLabel: string;
}

/** Flatten API results in stable module order (for keyboard navigation). */
export function flattenSearchResults(results: SearchResponse): FlatSearchRecord[] {
  const items: FlatSearchRecord[] = [];
  for (const moduleKey of GLOBAL_SEARCH_MODULE_ORDER) {
    const result = results[moduleKey];
    if (!result?.items?.length) continue;
    for (const item of result.items) {
      items.push({
        moduleKey,
        path: item.link,
        label: item.title,
        subtitle: item.subtitle,
        typeLabel: item.type,
      });
    }
  }
  for (const moduleKey of Object.keys(results)) {
    if ((GLOBAL_SEARCH_MODULE_ORDER as readonly string[]).includes(moduleKey)) continue;
    const result = results[moduleKey];
    if (!result?.items?.length) continue;
    for (const item of result.items) {
      items.push({
        moduleKey,
        path: item.link,
        label: item.title,
        subtitle: item.subtitle,
        typeLabel: item.type,
      });
    }
  }
  return items;
}

export function getOrderedSearchModules(results: SearchResponse): string[] {
  const ordered: string[] = GLOBAL_SEARCH_MODULE_ORDER.filter(
    (key) => (results[key]?.items?.length ?? 0) > 0,
  );
  for (const key of Object.keys(results)) {
    if (!ordered.includes(key) && (results[key]?.items?.length ?? 0) > 0) {
      ordered.push(key);
    }
  }
  return ordered;
}

export function countSearchResults(results: SearchResponse): number {
  return Object.values(results).reduce((sum, result) => sum + (result?.total ?? 0), 0);
}
